import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Icon, type IconKey } from '../icons';

interface SearchableItem {
  id: number | string;
  name: string;
  type: 'point' | 'line' | 'polygon' | 'sensor' | 'incident' | 'mission' | 'task-set';
  latitude?: number;
  longitude?: number;
  coordinates?: number[][];
  color?: string;
  extra?: string;
}

interface MapSearchProps {
  items: SearchableItem[];
  onSelect: (item: SearchableItem) => void;
}

const TYPE_ICONS: Record<string, IconKey> = {
  point: 'site',
  line: 'freehand',
  polygon: 'area',
  sensor: 'sensor',
  incident: 'threat',
  mission: 'mission',
  'task-set': 'taskset',
};

const TYPE_LABELS: Record<string, string> = {
  point: 'Point',
  line: 'Path',
  polygon: 'Area',
  sensor: 'Sensor',
  incident: 'Incident',
  mission: 'Mission',
  'task-set': 'Task Set',
};

export default function MapSearch({ items, onSelect }: MapSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    const matched = items.filter(item => item.name.toLowerCase().includes(q));
    matched.sort((a, b) => {
      const aStart = a.name.toLowerCase().startsWith(q) ? 0 : 1;
      const bStart = b.name.toLowerCase().startsWith(q) ? 0 : 1;
      if (aStart !== bStart) return aStart - bStart;
      return a.name.localeCompare(b.name);
    });
    return matched.slice(0, 20);
  }, [query, items]);

  const handleSelect = useCallback((item: SearchableItem) => {
    onSelect(item);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }, [onSelect]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [results]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (listRef.current && open) {
      const highlighted = listRef.current.querySelector('.map-search-result.highlighted') as HTMLElement;
      highlighted?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') {
        setQuery('');
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(i => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(i => (i - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        handleSelect(results[highlightIndex]);
        break;
      case 'Escape':
        setQuery('');
        setOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handleGlobalKey);
    return () => document.removeEventListener('keydown', handleGlobalKey);
  }, []);

  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchableItem[]> = {};
    for (const item of results) {
      if (!groups[item.type]) groups[item.type] = [];
      groups[item.type].push(item);
    }
    return groups;
  }, [results]);

  const flatResults = results;

  return (
    <div ref={containerRef} className="map-search-container">
      <div className="map-search-input-wrap">
        <svg className="map-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          className="map-search-input"
          type="text"
          placeholder="Search features…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.trim()) setOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {!query && (
          <kbd className="map-search-shortcut">⌘K</kbd>
        )}
        {query && (
          <button
            className="map-search-clear"
            onClick={() => { setQuery(''); setOpen(false); inputRef.current?.focus(); }}
            aria-label="Close"
          >
            <Icon name="clear" />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div className="map-search-dropdown" ref={listRef}>
          {flatResults.length === 0 ? (
            <div className="map-search-empty">No results for "{query}"</div>
          ) : (
            Object.entries(groupedResults).map(([type, groupItems]) => (
              <div key={type}>
                <div className="map-search-group-header">
                  <Icon name={TYPE_ICONS[type]} /> {TYPE_LABELS[type] || type}
                </div>
                {groupItems.map(item => {
                  const idx = flatResults.indexOf(item);
                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={`map-search-result ${idx === highlightIndex ? 'highlighted' : ''}`}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onClick={() => handleSelect(item)}
                    >
                      {item.color && (
                        <span
                          className="map-search-color-dot"
                          style={{ backgroundColor: item.color }}
                        />
                      )}
                      <span className="map-search-result-name">{highlightMatch(item.name, query)}</span>
                      {item.extra && (
                        <span className="map-search-result-extra">{item.extra}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function highlightMatch(text: string, query: string): React.ReactNode {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong style={{ color: 'var(--color-accent)' }}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}
